import { useCallback, useEffect, useRef, useState } from 'react';
import { getCompetitionSession, saveCompetitionStat, assetUrl } from '../../services/api';
import { COMPETITION_STATS, parseStat } from '../../utils/competition';
import { getRumaalColor } from '../../utils/colors';

// Recording a practice or a competition.
//
// A session is a date plus a label, and every dancer's six stats hang off it.
// Keeping them per session rather than as one running number is what lets the
// coach record an honest bad practice — the leaderboard averages, so one poor
// night moves a dancer a little instead of demoting them.
//
// Stats save themselves as they are typed, the same as the ceremony grid.

const CELL = 'w-full rounded-md border text-center text-base font-semibold outline-none transition-colors py-1.5';
const FIELD = 'rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15';

const GRID = 'grid grid-cols-[minmax(170px,1.4fr)_repeat(6,minmax(56px,1fr))_minmax(140px,1.2fr)] gap-2';

const today = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

export default function StatGrid({ teamId, sessions, onSaved }) {
  const [date, setDate] = useState(today);
  const [event, setEvent] = useState('');
  const [rows, setRows] = useState([]);
  const [saveState, setSaveState] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const timers = useRef({});
  const retries = useRef({});
  const pending = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCompetitionSession(teamId, { date, event });
      setRows(data.rows);
      setError('');
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Could not load that session');
    } finally {
      setLoading(false);
    }
  }, [teamId, date, event]);

  useEffect(() => {
    // Typing a session label shouldn't fire a request per keystroke.
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    const t = timers.current;
    const r = retries.current;
    return () => {
      Object.values(t).forEach(clearTimeout);
      Object.values(r).forEach(clearTimeout);
    };
  }, []);

  const push = useCallback(async (studentKey) => {
    const changes = pending.current[studentKey];
    if (!changes) return;
    delete pending.current[studentKey];

    try {
      await saveCompetitionStat(teamId, studentKey, { ...changes, recordedAt: date, event });
      if (!pending.current[studentKey]) {
        setSaveState((s) => ({ ...s, [studentKey]: 'saved' }));
        clearTimeout(retries.current[studentKey]);
      }
      onSaved?.();
    } catch (saveError) {
      setSaveState((s) => ({ ...s, [studentKey]: 'error' }));
      if (saveError.response?.status === 400) {
        setError(saveError.response.data?.error || 'That value was rejected');
        return;
      }
      // Anything else is the network. Keep the edits and try again quietly.
      pending.current[studentKey] = { ...changes, ...(pending.current[studentKey] || {}) };
      clearTimeout(retries.current[studentKey]);
      retries.current[studentKey] = setTimeout(() => push(studentKey), 4000);
    }
  }, [teamId, date, event, onSaved]);

  const change = (studentKey, field, value) => {
    setRows((current) => current.map((row) => (
      row.studentKey === studentKey ? { ...row, [field]: value } : row
    )));

    setSaveState((s) => ({ ...s, [studentKey]: 'saving' }));
    // Merged per dancer: a stat and a note typed within the debounce must not
    // cancel each other.
    pending.current[studentKey] = { ...(pending.current[studentKey] || {}), [field]: value };
    clearTimeout(timers.current[studentKey]);
    timers.current[studentKey] = setTimeout(() => push(studentKey), 600);
  };

  const setStat = (studentKey, key, raw) => {
    const parsed = parseStat(raw);
    if (parsed.invalid) return; // refuse as it is typed
    change(studentKey, key, parsed.value);
  };

  const unsaved = Object.values(saveState).filter((s) => s === 'saving' || s === 'error').length;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-white p-3">
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={FIELD} />
        </label>
        <label className="block flex-1">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">
            Practice or competition
          </span>
          <input
            value={event}
            onChange={(e) => setEvent(e.target.value)}
            placeholder="Sunday drills"
            list="l2b-session-events"
            className={`${FIELD} w-full`}
          />
          <datalist id="l2b-session-events">
            {[...new Set(sessions.map((s) => s.event).filter(Boolean))].map((label) => (
              <option key={label} value={label} />
            ))}
          </datalist>
        </label>
        <span className={`rounded-full px-3 py-1.5 text-xs font-medium ${
          unsaved === 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'
        }`}>
          {unsaved === 0 ? 'All stats saved' : `${unsaved} saving…`}
        </span>
      </div>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <div className="min-w-[860px]">
          <div className={`${GRID} items-end border-b-2 border-gray-900 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500`}>
            <span>Dancer</span>
            {COMPETITION_STATS.map((stat) => (
              <span key={stat.key} className="text-center" title={`${stat.label} out of 10`}>
                {stat.short}
              </span>
            ))}
            <span>Coach notes</span>
          </div>

          {rows.map((row) => (
            <div key={row.studentKey} className={`${GRID} items-center border-b border-gray-100 px-3 py-2`}>
              <div className="flex min-w-0 items-center gap-2">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
                  style={{ boxShadow: `0 0 0 2px ${getRumaalColor(row.rumaalRank)}, 0 0 0 3px rgba(0,0,0,.15)` }}
                >
                  {row.photoUrl
                    ? <img src={assetUrl(row.photoUrl)} alt="" className="h-full w-full object-cover" />
                    : <span className="text-[10px] font-semibold text-gray-400">
                        {row.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                      </span>}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-gray-900">{row.name}</div>
                  {row.role && <div className="truncate text-xs text-gray-500">{row.role}</div>}
                </div>
              </div>

              {COMPETITION_STATS.map((stat) => (
                <StatCell
                  key={stat.key}
                  value={row[stat.key]}
                  label={`${stat.label} for ${row.name}`}
                  onCommit={(value) => setStat(row.studentKey, stat.key, value)}
                />
              ))}

              <div className="flex items-center gap-1">
                <NotesField
                  value={row.coachNotes}
                  label={`Coach notes for ${row.name}`}
                  onCommit={(value) => change(row.studentKey, 'coachNotes', value)}
                />
                <SaveDot state={saveState[row.studentKey]} />
              </div>
            </div>
          ))}

          {!loading && rows.length === 0 && (
            <p className="p-8 text-center text-sm text-gray-500">
              Nobody is on this team yet — add dancers before recording a session.
            </p>
          )}
          {loading && <p className="p-8 text-center text-sm text-gray-500">Loading…</p>}
        </div>
      </div>

      <p className="mt-2 text-xs text-gray-500">
        Stats are 0–10 to one decimal place. Coach notes are shown to the dancer in their app,
        so write them as something a child should read.
      </p>
    </div>
  );
}

/**
 * One stat cell. Holds the text being typed rather than the number, so "8." and
 * "8.6" can be reached a keystroke at a time, and drops a keystroke that could
 * not lead anywhere valid so the cell always shows exactly what is stored.
 */
function StatCell({ value, label, onCommit }) {
  const [text, setText] = useState(value ?? '');
  const [lastSeen, setLastSeen] = useState(value);

  if (value !== lastSeen) {
    setLastSeen(value);
    setText(value ?? '');
  }

  const handle = (raw) => {
    const trimmed = raw.trim();
    if (parseStat(trimmed).invalid) return;
    setText(trimmed);
    onCommit(trimmed);
  };

  const empty = value === null || value === undefined;

  return (
    <input
      value={text}
      onChange={(e) => handle(e.target.value)}
      onFocus={(e) => e.target.select()}
      inputMode="decimal"
      aria-label={label}
      className={`${CELL} ${
        empty ? 'border-dashed border-gray-300 text-gray-400' : 'border-gray-300 text-gray-900'
      } focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20`}
    />
  );
}

function NotesField({ value, label, onCommit }) {
  const [text, setText] = useState(value ?? '');
  const [lastSeen, setLastSeen] = useState(value);

  if (value !== lastSeen) {
    setLastSeen(value);
    setText(value ?? '');
  }

  return (
    <textarea
      rows={2}
      value={text}
      onChange={(e) => { setText(e.target.value); onCommit(e.target.value); }}
      placeholder="What they should work on"
      aria-label={label}
      title={text}
      className="w-full resize-y rounded-md border border-gray-200 px-2 py-1.5 text-xs leading-snug outline-none placeholder:text-gray-300 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15"
    />
  );
}

function SaveDot({ state }) {
  if (!state || state === 'saved') return <span className="w-2 shrink-0" />;
  return (
    <span
      title={state === 'error' ? 'Not saved yet — still trying' : 'Saving'}
      className={`h-2 w-2 shrink-0 rounded-full ${state === 'error' ? 'bg-red-500' : 'bg-amber-400'}`}
    />
  );
}
